/**
 * Map container component
 * Base Leaflet map with common configurations
 */

import { useRef, useEffect, useMemo } from 'react';
import {
  MapContainer as LeafletMapContainer,
  TileLayer,
  ZoomControl,
  ScaleControl,
  useMap,
} from 'react-leaflet';
import type { Map as LeafletMap, LatLngBoundsExpression, LatLngExpression } from 'leaflet';
import { MAP_DEFAULTS } from '@/utils/constants';
import 'leaflet/dist/leaflet.css';

interface MapContainerProps {
  children?: React.ReactNode;
  center?: LatLngExpression;
  zoom?: number;
  bounds?: LatLngBoundsExpression;
  height?: number | string;
  width?: number | string;
  onMapReady?: (map: LeafletMap) => void;
  scrollWheelZoom?: boolean;
  dragging?: boolean;
  zoomControl?: boolean;
  scaleControl?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * Hook to fit map to bounds
 */
function FitBounds({ bounds }: { bounds?: LatLngBoundsExpression }) {
  const map = useMap();

  useEffect(() => {
    if (bounds) {
      map.fitBounds(bounds, { padding: [20, 20] });
    }
  }, [map, bounds]);

  return null;
}

/**
 * Hook to call onMapReady when map is ready
 */
function MapReadyHandler({ onMapReady }: { onMapReady?: (map: LeafletMap) => void }) {
  const map = useMap();

  useEffect(() => {
    if (onMapReady) {
      onMapReady(map);
    }
  }, [map, onMapReady]);

  return null;
}

export function MapContainerWrapper({
  children,
  center = MAP_DEFAULTS.center,
  zoom = MAP_DEFAULTS.initialZoom,
  bounds,
  height = 400,
  width = '100%',
  onMapReady,
  scrollWheelZoom = true,
  dragging = true,
  zoomControl = true,
  scaleControl = true,
  className,
  style,
}: MapContainerProps) {
  const containerStyle = useMemo(
    () => ({
      height: typeof height === 'number' ? `${height}px` : height,
      width: typeof width === 'number' ? `${width}px` : width,
      borderRadius: 8,
      overflow: 'hidden' as const,
      ...style,
    }),
    [height, width, style]
  );

  return (
    <div style={containerStyle} className={className}>
      <LeafletMapContainer
        center={center}
        zoom={zoom}
        scrollWheelZoom={scrollWheelZoom}
        dragging={dragging}
        zoomControl={false}
        style={{ height: '100%', width: '100%' }}
      >
        {/* Tile layer */}
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {/* Controls */}
        {zoomControl && <ZoomControl position="topright" />}
        {scaleControl && <ScaleControl position="bottomleft" />}

        {/* Auto-fit bounds */}
        {bounds && <FitBounds bounds={bounds} />}

        {/* Map ready callback */}
        {onMapReady && <MapReadyHandler onMapReady={onMapReady} />}

        {/* Child components */}
        {children}
      </LeafletMapContainer>
    </div>
  );
}

/**
 * Satellite/hybrid tile option
 */
export function SatelliteTileLayer() {
  return (
    <TileLayer
      attribution='Tiles &copy; Esri'
      url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
    />
  );
}

/**
 * Dark mode tile option
 */
export function DarkTileLayer() {
  return (
    <TileLayer
      attribution='&copy; <a href="https://carto.com/">CARTO</a>'
      url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
    />
  );
}

export { MapContainerWrapper as MapContainer };
export default MapContainerWrapper;
